// src/packages/chubb/pa/chubb_pa_series_a_2025/ChubbPASeriesADetails.jsx
import React, { useMemo } from "react";
import "./chubb_pa_series_a.css";

/**
 * Chubb — PA Series A (Plan 1–8 + Kid)
 * Zero-index safe: ไม่มี {index} ใน JSX
 * ใส่ตัวเลขจากตารางเอกสารลงในอาร์เรย์ PLANS/benefits/premiums ด้านล่างได้ทันที
 */

const fmt = (x) => (typeof x === "number" ? x.toLocaleString() : x ?? "—");

// ชื่อคอลัมน์ (ตามแผน)
const PLANS = ["แผน 1","แผน 2","แผน 3","แผน 4","แผน 5","แผน 6","แผน 7","แผน 8","บุตร Kid"];

// ===== สรุปผลประโยชน์หลักต่อแผน =====
// TODO: เติมตัวเลขจากเอกสารของคุณในแต่ละ array (หน่วยบาท)
const benefits = [
  { key: "life",   name: "1. อุบัติเหตุทั่วไป (ชีวิต/สูญเสียอวัยวะ/ทุพพลภาพถาวร)", vals: [100000,200000,300000,500000,700000,1000000,2000000,3000000,50000] },
  { key: "murder", name: "1.2 ฆาตกรรม/ทำร้ายร่างกาย",                     vals: [100000,200000,300000,500000,700000,1000000,2000000,3000000,50000] },
  { key: "moto",   name: "1.3 อุบัติเหตุรถจักรยานยนต์",                    vals: [50000,100000,150000,250000,350000,500000,1000000,1500000,25000] },
  { key: "holiday",name: "1.4 วันหยุดสาธารณะ (เพิ่มทุนพิเศษ)",             vals: [100000,200000,300000,500000,700000,1000000,2000000,3000000,50000] },
  { key: "funeral",name: "2. ค่าปลงศพ (เหตุอุบัติเหตุ)",                   vals: [10000,10000,10000,10000,10000,15000,15000,12000,5000] },
  { key: "med",    name: "3. ค่ารักษาพยาบาลต่อครั้ง (รวมมอเตอร์ไซค์)",     vals: [/* ใส่เลขตามแผน */] },
  { key: "dental", name: "4. ค่าทันตกรรมจากอุบัติเหตุ",                    vals: [/* ใส่เลขตามแผน */] },
  { key: "fract",  name: "5. กระดูกแตก/ร้าว/ทิ่มทะลุ (ตามตารางผลประโยชน์)", vals: [/* ใส่เลขตามแผน */] },
  { key: "hap",    name: "6. เฉพาะ Smart Money (HAP) *ถ้ามีในแผน",         vals: [/* กรอกหรือใส่ '—' */] },
];

// ===== ตารางเบี้ยรายปี (รวมภาษีอากร) =====
// แบ่งเป็น (A) PA1 เดี่ยว/ครอบครัว และ (B) PA2 (มีอุบัติเหตุส่วนบุคคล + เพิ่มเติมบางส่วน)
// ใส่ตัวเลขจากตารางเอกสารตามแผน (แกนคอลัมน์เท่ากับ PLANS)
const premiums = {
  PA1: [
    { key: "main",   label: "ผู้เอาประกันหลัก (Main Insured)",                   vals: [/* 215, 415, 615, 995, 1_395, 1_795, 3_295, 4_795, null */] },
    { key: "spouse", label: "ผู้เอาประกัน + คู่สมรส",                              vals: [/* ... */] },
    { key: "kids2",  label: "ผู้เอาประกัน + บุตร ไม่เกิน 2 คน",                    vals: [/* ... */] },
    { key: "kids3",  label: "ผู้เอาประกัน + บุตร ไม่เกิน 3 คน",                    vals: [/* ... */] },
    { key: "family", label: "ผู้เอาประกัน + คู่สมรส + บุตร ไม่เกิน 3 คน",           vals: [/* ... */] },
  ],
  PA2: [
    { key: "main",   label: "ผู้เอาประกันหลัก (Main Insured)",                   vals: [/* 275, 545, 805, 1_315, 1_795, 2_395, 4_435, 6_595, null */] },
    { key: "spouse", label: "ผู้เอาประกัน + คู่สมรส",                              vals: [/* ... */] },
    { key: "kids2",  label: "ผู้เอาประกัน + บุตร ไม่เกิน 2 คน",                    vals: [/* ... */] },
    { key: "kids3",  label: "ผู้เอาประกัน + บุตร ไม่เกิน 3 คน",                    vals: [/* ... */] },
    { key: "family", label: "ผู้เอาประกัน + คู่สมรส + บุตร ไม่เกิน 3 คน",           vals: [/* ... */] },
  ],
};

export default function ChubbPASeriesADetails({ onQuote }) {
  const hasMed = useMemo(() => (benefits.find(b => b.key === "med")?.vals || []).some(Boolean), []);

  return (
    <div className="pkg-detail chubb-pa">
      <h2>Chubb PA Series A — แผน 1–8 + Kid</h2>
      <p className="pkg-note">
        อายุรับ 15–70 ปี ต่ออายุได้ถึง 75 ปี • เด็ก 6 เดือน–23 ปี (ตามเงื่อนไข) •
        มีแผนครอบครัวให้เลือก (เดี่ยว/คู่สมรส/บุตร) • เบี้ยรวมอากรแสตมป์
      </p>

      {/* ตารางผลประโยชน์ */}
      <h3 className="pkg-section">ผลประโยชน์ต่อแผน</h3>
      <table className="pkg-table">
        <thead>
          <tr>
            <th className="pkg-left">ความคุ้มครอง</th>
            {PLANS.map((p) => <th key={`plan-${p}`}>{p}</th>)}
          </tr>
        </thead>
        <tbody>
          {benefits.map((b) => (
            <tr key={`bf-${b.key}`}>
              <td className="pkg-left">{b.name}</td>
              {(b.vals || []).map((v, i) => <td key={`bf-${b.key}-${i}`}>{fmt(v)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>

      {/* ตารางเบี้ย: PA1 */}
      <h3 className="pkg-section">เบี้ยรายปี (PA1)</h3>
      <table className="pkg-table">
        <thead>
          <tr>
            <th className="pkg-left">รูปแบบผู้เอาประกัน</th>
            {PLANS.map((p) => <th key={`pa1h-${p}`}>{p}</th>)}
          </tr>
        </thead>
        <tbody>
          {premiums.PA1.map((row) => (
            <tr key={`pa1-${row.key}`}>
              <td className="pkg-left">{row.label}</td>
              {(row.vals || []).map((v, i) => <td key={`pa1-${row.key}-${i}`}>{fmt(v)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>

      {/* ตารางเบี้ย: PA2 */}
      <h3 className="pkg-section">เบี้ยรายปี (PA2)</h3>
      <table className="pkg-table">
        <thead>
          <tr>
            <th className="pkg-left">รูปแบบผู้เอาประกัน</th>
            {PLANS.map((p) => <th key={`pa2h-${p}`}>{p}</th>)}
          </tr>
        </thead>
        <tbody>
          {premiums.PA2.map((row) => (
            <tr key={`pa2-${row.key}`}>
              <td className="pkg-left">{row.label}</td>
              {(row.vals || []).map((v, i) => <td key={`pa2-${row.key}-${i}`}>{fmt(v)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>

      {/* หมายเหตุ */}
      <ul className="pkg-bullets">
        <li>Kid = บุตรอายุ 6 เดือน–23 ปี ตามเงื่อนไขบริษัท</li>
        <li>แผน/วงเงินบางรายการอาจไม่คุ้มครองชั้นอาชีพบางประเภท</li>
        <li>โปรดตรวจสอบเงื่อนไข/ข้อยกเว้นในกรมธรรม์ก่อนตัดสินใจ</li>
      </ul>

      <div className="pkg-foot">
        <button className="pkg-quote-btn" onClick={onQuote} aria-label="ขอใบเสนอราคา Chubb PA Series A">
          ขอใบเสนอราคา
        </button>
      </div>
    </div>
  );
}
